import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getLead, getAuditState, getNameTestState } from "@/lib/storage";
import { EXPOSURE_META, fmtDate } from "@/lib/exposure";
import { BrandHeader } from "@/components/BrandHeader";

const JOEY_SMILE = "https://customer-assets.emergentagent.com/job_648de01e-2b76-4a92-ab16-261bd9620d49/artifacts/tzi0vidg_3.png";

export const Dashboard = () => {
  const navigate = useNavigate();
  const [lead, setLeadState] = useState(null);
  const [auditState, setAudit] = useState(null);
  const [nameState, setName] = useState(null);
  const [assessmentHistory, setAssessmentHistory] = useState([]);

  useEffect(() => {
    const l = getLead();
    if (!l) {
      navigate("/", { replace: true });
      return;
    }
    setLeadState(l);
    setAudit(getAuditState());
    setName(getNameTestState());
    // Fetch live assessment history for the trajectory chip on the tool card
    api.get("/assessment/history")
      .then((res) => setAssessmentHistory(res.data?.history || []))
      .catch(() => {});
  }, [navigate]);
  // Imported helpers (getLead, getQuizState, getAuditState, getNameTestState)
  // are module-level constants, so they're stable across renders and intentionally
  // omitted from deps. Same pattern applies in the other page components.

  if (!lead) return null;

  return (
    <div data-testid="dashboard-page">
      <BrandHeader showSignOut />

      {/* Welcome block */}
      <section className="brand-navy-band" style={{ paddingBottom: "3.5rem", paddingTop: "2.5rem" }}>
        <div className="container-wide" style={{ display: "grid", gridTemplateColumns: "96px 1fr", gap: "1.4rem", alignItems: "center", color: "var(--warm-white)" }}>
          <img
            src={JOEY_SMILE}
            alt="Joey Vitale"
            style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", border: "3px solid var(--yellow)" }}
            data-testid="dashboard-joey-photo"
          />
          <div>
            <h1 style={{ color: "var(--warm-white)", fontSize: "clamp(1.6rem, 4vw, 2.2rem)", margin: 0, letterSpacing: "-0.02em" }} data-testid="dashboard-greeting">
              Welcome, {lead.first_name}.
            </h1>
            <p style={{ margin: "0.5rem 0 0", color: "rgba(242,233,219,0.92)", maxWidth: 640 }}>
              You're in. Below are the free tools I built to help you figure out where your brand actually stands. Take them in any order. By the time you're done, you'll have a clearer read on your brand than most business owners ever get. Let's do this.
            </p>
            <p style={{ margin: "0.6rem 0 0", color: "var(--yellow)", fontWeight: 700, fontSize: "0.95rem" }}>— Joey Vitale, Trademark Lawyer</p>
          </div>
        </div>
      </section>

      {/* Three tool cards */}
      <section style={{ padding: "3.5rem 0" }}>
        <div className="container-wide">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.25rem" }}>

            <ToolCard
              testId="dashboard-tool-quiz"
              eyebrow="Start here · the snapshot"
              title="The Legal IQ Assessment"
              body="A 3-minute snapshot. 9 quick questions about your business — your stage, your assets, your worries. I'll build you a Priority Map showing the #1 thing to protect, what to watch, and what you're already getting right. Take it every quarter to track your trajectory."
              cta={assessmentHistory.length > 0 ? `View history & retake →` : `Start the Assessment →`}
              completion={null}
              to="/quiz"
              trajectory={assessmentHistory.length > 0 ? {
                count: assessmentHistory.length,
                lastDate: fmtDate(assessmentHistory[0]?.created_at),
                chips: [...assessmentHistory].reverse().map((h) => ({
                  level: h.exposure_level,
                  meta: EXPOSURE_META[h.exposure_level] || EXPOSURE_META.medium,
                })),
              } : null}
            />

            <ToolCard
              testId="dashboard-tool-audit"
              eyebrow="Get personal · the deep dive"
              title="Your Brand Protection Audit"
              body="The microscope on your specific brand. I'll ask sharper questions — the kind I'd ask a new client on a discovery call — then build you a 3-move Action Plan you can start this month. Take it when something material changes: a new product, a name shift, a copycat sighting."
              cta={auditState?.completed ? `View your Action Plan →` : `Run the Audit →`}
              completion={auditState?.completed ? `✓ Completed — View your Action Plan →` : null}
              to="/audit"
            />

            <ToolCard
              testId="dashboard-tool-name-test"
              eyebrow="Test a name"
              title="The Name Evaluator"
              body="Got a name in mind for your business, course, podcast, framework, or product? Paste it in and I'll tell you if it's the kind of name you can build on, or the kind that'll cost you later."
              cta={nameState?.completed ? `Test another →` : `Test a Name →`}
              completion={nameState?.completed && nameState.lastName ? `✓ Last name tested: ${nameState.lastName} — Test another →` : null}
              to="/name-test"
            />

            <ToolCard
              testId="dashboard-tool-ask"
              eyebrow="Ask me anything"
              title="Ask Joey Anything"
              body="Got a question about trademarks, naming, contracts, or copycats? Ask away. Plain English answers, no legalese."
              cta="Start asking →"
              to="/ask"
              avatar="https://customer-assets.emergentagent.com/job_brand-iq-check/artifacts/4uufr78g_deb056c-8c4-ed5-5d48-4283b526a245_ALV_7106_1_.jpeg"
              avatarPosition="center 35%"
            />
          </div>
        </div>
      </section>

      {/* Webinar CTA block */}
      <section className="brand-navy-band" style={{ padding: "3rem 0" }}>
        <div
          className="container-narrow training-row"
          style={{ color: "var(--warm-white)", display: "grid", gridTemplateColumns: "220px 1fr", gap: "2.2rem", alignItems: "center" }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_brand-iq-check/artifacts/yjj8ka9e_Indie_June_Color-151.jpg"
            alt="Joey Vitale"
            data-testid="dashboard-training-photo"
            style={{
              width: 220,
              height: 220,
              borderRadius: "50%",
              objectFit: "cover",
              objectPosition: "center 25%",
              border: "4px solid var(--yellow)",
              boxShadow: "6px 6px 0 0 var(--yellow)",
            }}
          />
          <div>
            <p className="eyebrow" style={{ color: "var(--yellow)" }}>The free training</p>
            <h2 style={{ color: "var(--warm-white)", margin: "0.5rem 0 0.8rem", fontSize: "clamp(1.4rem, 3.5vw, 1.9rem)" }}>
              "Protect Your Brand & Get Legally Legit"
            </h2>
            <p style={{ margin: "0 0 1.5rem", color: "rgba(242,233,219,0.92)" }}>
              Most entrepreneurs don't know the difference between using a brand name and <em>owning</em> one. In this free training, I'll walk you through my exact, step-by-step method for calling dibs on your trademarks — so you never have to worry about rebranding.
            </p>
            <p style={{ margin: "0 0 1.5rem", color: "rgba(242,233,219,0.8)", fontStyle: "italic" }}>
              Live, free, and worth your time.
            </p>
            <a
              href="https://indielaw.easywebinar.live/gettrademarked"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-yellow"
              data-testid="dashboard-webinar-cta"
            >
              Save my spot →
            </a>
          </div>
        </div>
        <style>{`@media (max-width: 640px) {
          .training-row { grid-template-columns: 1fr !important; gap: 1.3rem !important; justify-items: center; text-align: left; }
          .training-row > img { width: 150px !important; height: 150px !important; }
        }`}</style>
      </section>

      {/* Book mention */}
      <section style={{ padding: "2.5rem 0" }}>
        <div
          className="container-narrow book-row"
          data-testid="dashboard-book-callout"
          style={{
            display: "grid",
            gridTemplateColumns: "120px 1fr",
            gap: "1.4rem",
            alignItems: "center",
            background: "var(--warm-white-soft)",
            border: "1px solid var(--hairline)",
            borderTop: "4px solid var(--cobalt)",
            padding: "1.25rem 1.4rem",
          }}
        >
          <a
            href="https://a.co/d/0isDjKfG"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="dashboard-book-cover-link"
            style={{ display: "block", lineHeight: 0 }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_brand-iq-check/artifacts/6art66fr_61Mee%2Bi5-9L._AC_UF1000%2C1000_QL80_.jpg"
              alt="Legally Legit book cover by Joey Vitale"
              data-testid="dashboard-book-cover"
              style={{
                width: 120,
                height: "auto",
                display: "block",
                boxShadow: "5px 5px 0 0 var(--yellow)",
                border: "1px solid var(--navy)",
              }}
            />
          </a>
          <p style={{ margin: 0, color: "var(--midnight)" }}>
            Want to go deeper? My book <em>Legally Legit</em> covers everything we touch on here and a lot more.{" "}
            <a href="https://a.co/d/0isDjKfG" target="_blank" rel="noopener noreferrer" data-testid="dashboard-book-link">
              Get it on Amazon →
            </a>
          </p>
        </div>
        <style>{`@media (max-width: 640px) {
          .book-row { grid-template-columns: 90px 1fr !important; gap: 1rem !important; }
          .book-row img { width: 90px !important; }
        }`}</style>
      </section>
    </div>
  );
};

const ToolCard = ({ testId, eyebrow, title, body, cta, completion, to, trajectory, avatar, avatarPosition }) => (
  <Link
    to={to}
    className="card-paper"
    data-testid={testId}
    style={{ textDecoration: "none", color: "inherit", display: "flex", flexDirection: "column", position: "relative" }}
  >
    {avatar && (
      <img
        src={avatar}
        alt=""
        aria-hidden="true"
        data-testid={`${testId}-avatar`}
        style={{
          position: "absolute",
          top: "1rem",
          right: "1rem",
          width: 56,
          height: 56,
          borderRadius: "50%",
          objectFit: "cover",
          objectPosition: avatarPosition || "center 25%",
          border: "3px solid var(--yellow)",
          boxShadow: "3px 3px 0 0 var(--navy)",
        }}
      />
    )}
    <p className="eyebrow" style={avatar ? { paddingRight: 70 } : undefined}>{eyebrow}</p>
    <h3 style={{ margin: "0.4rem 0 0.6rem", fontSize: "1.35rem", paddingRight: avatar ? 70 : 0 }}>{title}</h3>
    <p style={{ margin: 0, color: "var(--midnight)", flex: 1 }}>{body}</p>

    {trajectory && (
      <div style={{ marginTop: "0.9rem" }} data-testid={`${testId}-trajectory`}>
        <p style={{ margin: "0 0 0.3rem", color: "var(--cobalt)", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Your brand exposure trajectory
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap", marginBottom: "0.35rem" }}>
          {trajectory.chips.map((c, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
              <span
                title={`Brand exposure: ${c.meta.label}`}
                style={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  padding: "0.18rem 0.42rem",
                  background: c.meta.color,
                  color: "var(--warm-white)",
                  letterSpacing: "0.05em",
                }}
              >
                {c.meta.label}
              </span>
              {i < trajectory.chips.length - 1 && <span style={{ color: "var(--cobalt)", fontWeight: 700, fontSize: "0.75rem" }}>→</span>}
            </span>
          ))}
        </div>
        <p style={{ margin: 0, color: "var(--cobalt)", fontSize: "0.78rem", fontWeight: 700 }}>
          Last taken {trajectory.lastDate} · {trajectory.count} {trajectory.count === 1 ? "run" : "runs"} · lower = more protected
        </p>
      </div>
    )}

    <div className="divider-rule" />
    {completion ? (
      <span style={{ color: "var(--evergreen)", fontWeight: 700 }} data-testid={`${testId}-completion`}>{completion}</span>
    ) : (
      <span style={{ color: "var(--cobalt)", fontWeight: 700 }} data-testid={`${testId}-cta`}>{cta}</span>
    )}
  </Link>
);
